import React, {useCallback, useRef, useState} from "react";
import {shallowEqual, useSelector} from "react-redux";
import * as c from "../../../constants";
import {useStyles} from "./EntityDropDown.styles";
import ContextCard, {DetailTransition} from "../../Entities/ContextList/ContextCard";
import {ChildrenNumberIcon, EntityTypeIcons} from "../Desktop/Icons";
import {selectEntityForOrgTree} from "../../../state/slices/OrgTree";
import {Box, Button, Dialog, DialogActions, DialogContent, Slide, Typography} from "@material-ui/core";
import {selectContextEntity, selectEntityById} from "../../../state/slices/CoreEntity";
import {AddEntityIcon, EntityDetailsIcon} from "../Shared";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import {useEntitiesWithSeverity, useEntitySelectedContext, useSeverityState} from "../Hooks";
import EntityExpandedView from "../../EntityCard/Expanded";
import EntityProvisionButton from "../../Entities/Modules/EntityProvisionButton";
import EntityDeleteButton from "../../Entities/Modules/EntityDeleteButton";
import DeviceActionMenu from "../../EntityCard/DeviceActionMenu";
import _ from "lodash";
import useHasPermissions from "../../../hooks/useHasPermissions";
import EntityCard from "../../EntityCard";
import {useFeatureFlag} from "../../../hooks/useFeatureFlags";
import {useHistory} from "react-router-dom";
import {useListening} from "../../../hooks/useDeviceListener/useDeviceListenerContext";

export const EntityDropDown = ({title, entities, hasChildren, entityID}) => {
    const classes = useStyles();
    const {entityDetailsState, handleEntityDetailSelection, onSelectedEntity} = useEntitySelectedContext();
    const {isBeingListened} = useListening?.() || {isBeingListened: () => false};

    const entity = useSelector((state) => {
        if(entityDetailsState.entityID && entityDetailsState.entityID !== entityID) return selectEntityForOrgTree(entityDetailsState.entityID)(state)
        return selectEntityForOrgTree(entityID)(state)
    }, shallowEqual);
    const {entitiesWithSeverity} = useEntitiesWithSeverity();
    const contextEntity = useSelector((state) => {
        return selectContextEntity(state)
    }, shallowEqual);
    const severityClass = useSeverityState(entitiesWithSeverity[entity?.entityID]?.SeverityInfo);
    const {hasPermissions} = useHasPermissions();
    const provisionDevicePermission = hasPermissions([
        "overview.devices.provisiondevice",
    ]);

    const history = useHistory();

    const contextID = useSelector(state => state.coreEntities.ContextID, shallowEqual);
    const contractNestFeature = useFeatureFlag("Contract Nest");
    const [isExpanded, setIExpanded] = useState(contextEntity.name === title);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const containerRef = useRef(null);
    const touchStartXRef = useRef(0);
    const isSwiping = useRef(false);
    const [isRevealed, setIsRevealed] = useState(false);

    const onEntityExpanding = () => {
        // Only expand if not swiping
        if (!isSwiping.current) {
            setIExpanded((prev) => !prev);
        }
    }


    const onDelete = useCallback(
        (entity) => {
            history.go(0);

        },
        [contextID]
    );


    const handleTouchStart = (e) => {
        touchStartXRef.current = e.touches[0].clientX;
        isSwiping.current = false;
    };

    const handleTouchMove = (e) => {
        if (touchStartXRef.current === 0) return;

        const touchX = e.touches[0].clientX;
        const diff = touchStartXRef.current - touchX;

        isSwiping.current = true;

        // Handle both left swipe (to open) and right swipe (to close)
        if (isRevealed) {
            // If already open, only allow right swipes to close (negative diff)
            if (diff < 0) {
                // Right swipe reduces the offset
                const newOffset = Math.max(0, 100 + diff);
                setSwipeOffset(newOffset);
                e.preventDefault();
            } else {
                // Keep fully open
                setSwipeOffset(100);
            }
        } else {
            // If closed, only allow left swipes to open (positive diff)
            if (diff > 5) {
                // Left swipe - limit to 100px max
                const newOffset = Math.min(diff, 100);
                setSwipeOffset(newOffset);
                e.preventDefault();
            }
        }
    };

    const handleTouchEnd = () => {
        // If swipe offset is more than 50px, keep it open
        if (swipeOffset > 50) {
            setSwipeOffset(100);
            setIsRevealed(true);
        } else {
            setSwipeOffset(0);
            setIsRevealed(false);
        }
        touchStartXRef.current = 0;
    };

    const cardState = useSelector((state) => {

        let entity = selectEntityById(state, entityID);

        if(entityDetailsState.entityID !== entityID){
            entity = selectEntityById(state, entityDetailsState.entityID);
        }
        return {
            laneClosed:
                _.find(entity?.settings, {name: "laneclosedsign"})?.value == "true",
            disabled: entity?.state != null && entity?.state?.Disabled == true,
            lotFull: entity?.state != null && entity?.state?.Occupancy?.Full == true,
            entityType: entity?.typeid,
            deviceMode:
                _.find(entity?.settings ?? [], {name: "devicemode"})?.value ?? null,
        };
    }, shallowEqual);

    const closeExpanded = async () => {
        if (entity.typeID === c.ENTITY_TYPE.Device) {
            await onSelectedEntity({entityid: entity.parentEntityID});
            handleEntityDetailSelection(entity.parentEntityID, false);
            return
        }
        if (entity.typeID === c.ENTITY_TYPE.Tenant) {
            await onSelectedEntity({entityid: contextID});
            handleEntityDetailSelection(contextID, false);
            return
        }
        if (entity.typeID === c.ENTITY_TYPE.Facility) {
            await onSelectedEntity({entityid: contextID});
            handleEntityDetailSelection(contextID, false);
            return
        }
        if(!entity.hasChildren && entity.parentEntityTypeID !== c.ENTITY_TYPE.Tenant) {
            await onSelectedEntity({entityid: entity.parentEntityID});
            handleEntityDetailSelection(entity.parentEntityID, false);
            return
        }
        if(!entity.hasChildren) {
            await onSelectedEntity({entityid: contextID});
            handleEntityDetailSelection(contextID, false);
        }
        await onSelectedEntity({entityid: entity.parentEntityID});
        handleEntityDetailSelection(entity.parentEntityID, false);
    };
    const handleChildrenIconClick = (e) => {
        e.stopPropagation(); // Prevent the container's onClick from firing
        if(!isRevealed){
            setSwipeOffset(100);
            setIsRevealed(true);
        }else {
            setSwipeOffset(0);
            setIsRevealed(false);
        }
    };

    if (entity?.typeid === c.ENTITY_TYPE.Area && entity.parententityid === contextID && !contractNestFeature) return null;

    return (
        <>
            {entities?.length > 0 &&
                <div>
                    <div className={classes.swipeContainer}>
                        <div
                            ref={containerRef}
                            className={`${classes.parentEntityCardContainer} nested-${entity.depth}`}
                            style={{
                                transform: `translateX(-${swipeOffset}px)`,
                            }}
                            onClick={onEntityExpanding}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                            {entity &&
                                <Box display="flex" alignItems="center">
                                    <EntityTypeIcons
                                        entityType={entity?.typeID || entity?.typeid}
                                        className={classes.entityTypeIcon}
                                        style={{
                                            width: 35,
                                            height: 35,
                                            paddingLeft: "8px"
                                        }}
                                    />
                                </Box>
                            }

                            <div className={classes.entityTitleContainer}>
                                <Typography variant="h6" className={classes.entityTitle}>{title}</Typography>
                                {!isExpanded &&
                                    <ExpandMoreIcon className={classes.expandIcon}/>
                                }
                                {isExpanded &&
                                    <ExpandLessIcon className={classes.expandIcon}/>
                                }
                            </div>
                            <ChildrenNumberIcon
                                entityID={entity?.entityid}
                                entities={entities}
                                className={`${classes.chip} ${classes[severityClass]}`}
                                onClick={handleChildrenIconClick}
                                style={{cursor: "pointer"}}
                            />
                        </div>
                        <div className={classes.hiddenContent}>
                            <AddEntityIcon entityId={entityID}/>
                            <EntityDetailsIcon entityId={entityID}/>
                        </div>
                    </div>

                    {isExpanded && entities?.length > 0 &&
                        entities?.sort((a, b) => {
                            const entityTypeA = c.ENTITY_TYPE[a.type]
                            const entityTypeB = c.ENTITY_TYPE[b.type]
                            return entityTypeA - entityTypeB
                        })?.map((entity) => {
                            const entityType = c.ENTITY_TYPE[entity.type]
                            if (entity?.typeid === c.ENTITY_TYPE.Area && entity.parententityid === contextID && !contractNestFeature) return null;

                            if (entityType !== c.ENTITY_TYPE.Device) {
                                return (
                                    <EntityDropDown
                                        key={entity?.entityid}
                                        entityID={entity?.entityid}
                                        title={entity?.name}
                                        entities={entity?.children}
                                        hasChildren={entity.hasChildren}
                                    />
                                )
                            }
                            return (
                                <Slide key={entity.entityid} direction="right" in={isExpanded} mountOnEnter
                                       unmountOnExit>
                                    <div key={entity.entityid} className={classes.entityCardContainer}>
                                        <EntityCard
                                            entityID={entity.entityid}
                                            tiled={false}
                                            onDelete={onDelete}
                                        />
                                    </div>
                                </Slide>

                            )
                        })
                    }

                    <Dialog
                        fullScreen={true}
                        TransitionComponent={DetailTransition}
                        scroll="paper"
                        open={entityDetailsState.isOpen}
                    >
                        <DialogContent dividers={true}>
                            <EntityExpandedView entityId={entityDetailsState.entityID}/>
                        </DialogContent>
                        <DialogActions style={{display: "flex"}}>
                            {cardState.entityType == c.ENTITY_TYPE.Device && (
                                <>
                                    {provisionDevicePermission && (
                                        <EntityProvisionButton
                                            entityID={entityDetailsState.entityID}
                                            isBeingListened={isBeingListened(entityDetailsState.entityID)}
                                        />
                                    )}
                                    <EntityDeleteButton
                                        entityID={entityDetailsState.entityID}
                                        variant="outlined"
                                        size="small"
                                        color="secondary"
                                        isDeviceControl={true}
                                        isBeingListened={isBeingListened(entityDetailsState.entityID)}

                                    />
                                    <div style={{flex: "1 0 0"}}/>
                                    <DeviceActionMenu
                                        entityID={entityDetailsState.entityID}
                                        entityName={entityDetailsState.entityID}
                                        deviceMode={cardState.deviceMode}
                                    />
                                    <Typography>|</Typography>
                                </>
                            )}

                            <Button
                                onClick={closeExpanded}
                                color="primary"
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
            }
        </>
    )
}
